/**
 * VendorMenuItemRow - Single menu item row with inline edit/delete
 * @atomic @idempotent
 */

import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { getInputClasses, smallInputClasses } from '../../styles/inputStyles'

const VendorMenuItemRow = memo(function VendorMenuItemRow({ item, onSave, onDelete }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    name: item.name || '',
    price: item.price != null ? String(item.price) : '',
    description: item.description || '',
    offeringType: item.offeringType || 'item',
    durationMinutes: item.durationMinutes != null ? String(item.durationMinutes) : '',
  })

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }, [])

  const handleSave = useCallback(() => {
    if (!form.name.trim()) return
    onSave(item.id, {
      name: form.name.trim(),
      price: form.price ? parseFloat(form.price) : undefined,
      description: form.description.trim() || undefined,
      offeringType: form.offeringType || 'item',
      durationMinutes: form.durationMinutes ? parseInt(form.durationMinutes, 10) : undefined,
    })
    setEditing(false)
  }, [form, item.id, onSave])

  const handleCancel = useCallback(() => {
    setForm({
      name: item.name || '',
      price: item.price != null ? String(item.price) : '',
      description: item.description || '',
      offeringType: item.offeringType || 'item',
      durationMinutes: item.durationMinutes != null ? String(item.durationMinutes) : '',
    })
    setEditing(false)
  }, [item])

  if (editing) {
    return (
      <li className="rounded-lg bg-gray-50 px-3 py-2 space-y-2">
        <input name="name" value={form.name} onChange={handleChange}
          className={getInputClasses(false)} maxLength={100} autoFocus />
        <div className="flex gap-2">
          <select name="offeringType" value={form.offeringType} onChange={handleChange}
            className="flex-1 rounded-lg border border-gray-300 text-xs px-2 py-1.5 bg-white">
            <option value="item">{t('vendor.menu.typeItem', 'Item')}</option>
            <option value="service">{t('vendor.menu.typeService', 'Service')}</option>
            <option value="package">{t('vendor.menu.typePackage', 'Package')}</option>
          </select>
          <input name="price" value={form.price} onChange={handleChange}
            placeholder="$0.00" className="flex-1 rounded-lg border border-gray-300 text-xs px-2 py-1.5" maxLength={20} />
          <input name="durationMinutes" type="number" min="1" max="480" value={form.durationMinutes} onChange={handleChange}
            placeholder={t('vendor.menu.durationMin', 'min')} className="w-16 rounded-lg border border-gray-300 text-xs px-2 py-1.5" />
        </div>
        <input name="description" value={form.description} onChange={handleChange}
          placeholder={t('vendor.menu.description', 'Description')} className={smallInputClasses} maxLength={255} />
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={handleSave}
            className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900">
            <CheckIcon className="h-3.5 w-3.5" /> {t('common.save', 'Save')}
          </button>
          <button type="button" onClick={handleCancel}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-3.5 w-3.5" /> {t('common.cancel', 'Cancel')}
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className="rounded-lg bg-gray-50 px-3 py-2">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-medium text-gray-800">{item.name}</p>
            {item.offeringType && item.offeringType !== 'item' && (
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                item.offeringType === 'service' ? 'bg-teal-100 text-teal-700' : 'bg-purple-100 text-purple-700'
              }`}>{item.offeringType}</span>
            )}
            {item.durationMinutes && (
              <span className="text-[10px] text-gray-400">{item.durationMinutes} min</span>
            )}
          </div>
          {item.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{item.description}</p>}
        </div>
        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
          {item.price != null && (
            <span className="text-xs font-semibold text-gray-700">${Number(item.price).toFixed(2)}</span>
          )}
          <button type="button" onClick={() => setEditing(true)}
            className="text-gray-400 hover:text-primary transition-colors">
            <PencilIcon className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => onDelete(item.id)}
            className="text-gray-400 hover:text-red-500 transition-colors">
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </li>
  )
})

export default VendorMenuItemRow
